/**
 * visa/models.ts — types for Module #3, the Roamer Visa™.
 *
 * A platform MODULE, not a core engine. It organizes travel permissions —
 * it does NOT replace official government information and NEVER presents
 * unofficial information as legal advice. Every piece of guidance carries a
 * provenance class so the four information kinds are always distinguished.
 */

// ─── Provenance & disclaimers (mandatory on all guidance) ─────────────────────

export const VISA_PROVENANCES = ['official', 'community', 'prediction', 'historical'] as const;
export type VisaProvenance = (typeof VISA_PROVENANCES)[number];

export const PROVENANCE_LABELS: Record<VisaProvenance, string> = {
  official: 'Official government information',
  community: 'Verified community experience',
  prediction: 'Prediction',
  historical: 'Historical knowledge',
};

export const LEGAL_DISCLAIMER =
  'This is organizational guidance, not legal advice. Always confirm with official government sources.';

// ─── Visa types & status ───────────────────────────────────────────────────────

export const VISA_TYPES = [
  'tourist',
  'visaFree',
  'digitalNomad',
  'business',
  'student',
  'work',
  'retirement',
  'transit',
] as const;
export type VisaType = (typeof VISA_TYPES)[number] | string; // future types allowed

export type VisaStatus = 'active' | 'expiringSoon' | 'expired' | 'none';

export interface VisaRecord {
  country: string;
  type: VisaType;
  entryDate: number;
  /** Planned exit; null if open. */
  exitDate: number | null;
  expiresAt: number;
  extensionEligible: boolean;
  /** Days before expiry when an extension can be filed; null if unknown. */
  extensionWindowDays: number | null;
  notes: string;
}

// ─── Profile ───────────────────────────────────────────────────────────────────

export interface EmergencyContact {
  id: string;
  name: string;
  relation: string;
  phone: string;
}

export interface VisaProfile {
  passportCountry: string | null;
  passportExpiresAt: number | null;
  currentCountry: string | null;
  currentVisa: VisaRecord | null;
  visaStatus: VisaStatus;
  /** Days until visa expiry; null without an active visa. */
  daysRemaining: number | null;
  extensionEligible: boolean | null;
  /** Known follow-on options, from country knowledge (with provenance). */
  nextVisaOptions: CountryVisaOption[];
  emergencyContacts: EmergencyContact[];
}

// ─── Country knowledge ─────────────────────────────────────────────────────────

/** Every knowledge item names its provenance — the four kinds are never mixed. */
export interface KnowledgeItem {
  text: string;
  provenance: VisaProvenance;
  /** Where it came from (url/description); '' when not recorded. */
  source: string;
  recordedAt: number;
}

export interface CountryVisaOption {
  type: VisaType;
  description: string;
  provenance: VisaProvenance;
}

export interface CountryKnowledge {
  country: string;
  visaRequirements: KnowledgeItem[];
  entryRules: KnowledgeItem[];
  extensionOptions: KnowledgeItem[];
  /** Only where legally applicable — items must state legality context. */
  borderRunInfo: KnowledgeItem[];
  embassyLocations: KnowledgeItem[];
  governmentResources: KnowledgeItem[];
  healthRequirements: KnowledgeItem[];
  arrivalRequirements: KnowledgeItem[];
  futurePolicyChanges: KnowledgeItem[];
  visaOptions: CountryVisaOption[];
  /** Ease-of-entry score 1–5 for the user's passport; null if unassessed. */
  entryEase: number | null;
}

export const KNOWLEDGE_SECTIONS = [
  'visaRequirements',
  'entryRules',
  'extensionOptions',
  'borderRunInfo',
  'embassyLocations',
  'governmentResources',
  'healthRequirements',
  'arrivalRequirements',
  'futurePolicyChanges',
] as const;
export type KnowledgeSection = (typeof KNOWLEDGE_SECTIONS)[number];

// ─── Documents (storage REFERENCES only — no file contents) ───────────────────

export const DOCUMENT_KINDS = [
  'passport',
  'visa',
  'insurance',
  'vaccinationRecord',
  'emergencyContacts',
  'supporting',
] as const;
export type DocumentKind = (typeof DOCUMENT_KINDS)[number];

export interface DocumentRef {
  id: string;
  kind: DocumentKind;
  title: string;
  /** Opaque reference (path/url/locator). Encrypted storage is a future feature. */
  ref: string;
  addedAt: number;
}

// ─── Reminders & alerts ────────────────────────────────────────────────────────

export type ReminderSeverity = 'info' | 'warning' | 'urgent';

export interface VisaReminder {
  /** Deterministic — same condition never fires twice. */
  id: string;
  severity: ReminderSeverity;
  message: string;
  provenance: VisaProvenance;
  at: number;
}

// ─── Dashboard ─────────────────────────────────────────────────────────────────

export interface VisaDashboard {
  status: VisaStatus;
  daysRemaining: number | null;
  countdownLabel: string;
  passportValid: boolean | null;
  passportDaysRemaining: number | null;
  extensionWindowOpen: boolean | null;
  upcomingRequirements: string[];
  requiredDocumentsMissing: DocumentKind[];
  disclaimer: string;
}

// ─── Travel planning ───────────────────────────────────────────────────────────

export interface TravelPlan {
  /** Suggested departure with buffer before expiry; null without a visa. */
  suggestedDepartureAt: number | null;
  /** When to file an extension; null if ineligible/unknown. */
  suggestedExtensionAt: number | null;
  /** From recorded country knowledge only — never invented. */
  recommendedDestinations: { country: string; reason: string; provenance: VisaProvenance }[];
  easierEntryCountries: string[];
  disclaimer: string;
}

// ─── Privacy / export ──────────────────────────────────────────────────────────

export interface VisaPrivacy {
  /** Travel documents are private by default. */
  shareStatus: boolean;
}

export const DEFAULT_VISA_PRIVACY: VisaPrivacy = { shareStatus: false };

export interface VisaExport {
  exportedAt: number;
  profile: VisaProfile;
  visaHistory: VisaRecord[];
  countryKnowledge: CountryKnowledge[];
  documents: DocumentRef[];
  privacy: VisaPrivacy;
}
